import { REPORTING_SCOPES, type ReportingScope, type ScopePreview, type ScopePreviews } from "@packages/core/organization/scope.ts";
import { GridList, GridListItem } from "@/components/ui/grid-list.tsx";
import { MenuTrigger } from "@/components/ui/menu.tsx";
import { Popover, PopoverContent } from "@/components/ui/popover.tsx";
import { Sheet, SheetContent } from "@/components/ui/sheet.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { CheckIcon } from "@heroicons/react/20/solid";
import { BuildingOffice2Icon, ChevronDownIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useLoadingIndicator } from "../hooks/useLoadingIndicator.tsx";
import { PersonAvatar } from "../people/PersonAvatar.tsx";
import { useSession } from "../session/SessionContext.tsx";
import { scopeI18NextInstanceKey } from "./i18n/i18next.ts";

const PREVIEW_KEY_BY_SCOPE: Record<ReportingScope, keyof ScopePreviews> = {
    "reporting-line": "reportingLine",
    "direct-reports": "directReports",
    organization: "organization"
};

const PLACEHOLDER_PREVIEW: ScopePreview = {
    previewMembers: [],
    totalCount: 10
};

const PLACEHOLDER_PREVIEWS: ScopePreviews = {
    reportingLine: PLACEHOLDER_PREVIEW,
    directReports: PLACEHOLDER_PREVIEW,
    organization: PLACEHOLDER_PREVIEW
};

export const MENU_ITEM_CLASS_NAME = "disabled:opacity-50 selected:bg-primary-subtle selected:text-primary selected:font-semibold font-medium transition-colors [&>svg]:text-muted-fg";

export interface TrailingCheckProps {
    isSelected: boolean;
}

export function TrailingCheck({ isSelected }: TrailingCheckProps) {
    if (!isSelected) {
        return null;
    }

    return <CheckIcon aria-hidden="true" className="text-primary size-4 shrink-0" />;
}

export interface ScopeCountProps {
    count: number;
    isLoading: boolean;
}

export function ScopeCount({ count, isLoading }: ScopeCountProps) {
    return (
        <Skeleton isLoading={isLoading} inert={isLoading}>
            <div>
                <div className="text-muted-fg group-selected:text-primary! flex items-center gap-1.5">
                    <UserGroupIcon aria-hidden="true" className="size-4 text-current" />
                    <span className="text-sm tabular-nums">{count}</span>
                </div>
            </div>
        </Skeleton>
    );
}

interface SegmentCountProps {
    count: number;
    isLoading: boolean;
}

function SegmentCount({ count, isLoading }: SegmentCountProps) {
    return (
        <Skeleton isLoading={isLoading} inert={isLoading}>
            <div>
                <div className="group-selected:text-primary-subtle-fg text-muted-fg tabular-nums">
                    <span>{count}</span>
                </div>
            </div>
        </Skeleton>
    );
}

// intent-ui-deviation: org logo kept as raw <img> -- Intent Avatar has no building-icon fallback and adds an outline ring this pill doesn't want (mirrors AuthenticatedLayout's workspace pill).
interface OrganizationLogoProps {
    imageUrl?: string;
    size: "sm" | "md";
}

function OrganizationLogo({ imageUrl, size }: OrganizationLogoProps) {
    const wrapperSize = size === "sm" ? "size-6" : "size-8";
    const iconSize = size === "sm" ? "size-3.5" : "size-4";
    return (
        <span data-slot="avatar" className={`bg-fg/5 inline-flex ${wrapperSize} shrink-0 items-center justify-center overflow-hidden rounded-full`}>
            {imageUrl ? <img src={imageUrl} alt="" className="me-0! size-full object-cover" /> : <BuildingOffice2Icon aria-hidden="true" className={`me-0! ${iconSize}`} />}
        </span>
    );
}

export interface ScopeSelectorSegmentsSectionSlots {
    search?: ReactNode;
    rows?: ReactNode;
    footer?: ReactNode;
}

export interface ScopeSelectorProps {
    scope: ReportingScope;
    onScopeChange: (scope: ReportingScope) => void;
    previews: ScopePreviews | undefined;
    isPreviewsPending: boolean;
    segmentId?: string | null;
    onSegmentChange?: (segmentId: string | null) => void;
    activeSegmentName?: string;
    hasAdHocFilter?: boolean;
    trailingContent?: ReactNode;
    renderSegmentsSection?: (context: { onRequestClose: () => void }) => ScopeSelectorSegmentsSectionSlots;
    onOpenChange?: (isOpen: boolean) => void;
    availableScopes?: ReadonlyArray<ReportingScope>;
}

export function ScopeSelector({ scope, onScopeChange, previews, isPreviewsPending, segmentId = null, onSegmentChange, activeSegmentName, hasAdHocFilter = false, trailingContent, renderSegmentsSection, onOpenChange, availableScopes = REPORTING_SCOPES }: ScopeSelectorProps) {
    const i18nextInstance = useI18nextInstance(scopeI18NextInstanceKey);
    const { t } = useTranslation("ScopeSelector", { i18n: i18nextInstance });
    const session = useSession();
    const [isOpen, setIsOpen] = useState(false);
    const handleOpenChange = useCallback(
        (nextIsOpen: boolean) => {
            setIsOpen(nextIsOpen);
            onOpenChange?.(nextIsOpen);
        },
        [onOpenChange]
    );

    const isMobile = useIsMobile();

    const isLoading = useLoadingIndicator(isPreviewsPending);
    const resolvedPreviews = isLoading ? PLACEHOLDER_PREVIEWS : (previews ?? PLACEHOLDER_PREVIEWS);
    const shouldRenderPreviews = isLoading || !!previews;

    const isSegmentActive = !!segmentId && !!activeSegmentName;

    const isOrganization = scope === "organization";
    const dropdownKey: ReportingScope = isOrganization ? "organization" : "reporting-line";
    const currentUserName = session?.displayName || session?.firstName || session?.email;
    const currentUserPerson = currentUserName ? { displayName: currentUserName, imageUrl: session?.image } : undefined;

    const [lastTeamScope, setLastTeamScope] = useState<ReportingScope>(scope === "organization" ? "reporting-line" : scope);

    if (scope !== "organization" && scope !== lastTeamScope) {
        setLastTeamScope(scope);
    }

    const handleSelection = useCallback(
        (keys: "all" | Set<Key>) => {
            if (keys === "all") {
                return;
            }
            const [key] = keys;
            if (key === "organization") {
                onSegmentChange?.(null);
                onScopeChange("organization");
            } else if (key === "reporting-line") {
                onSegmentChange?.(null);
                onScopeChange(lastTeamScope);
            } else {
                onSegmentChange?.(String(key));
            }
            setIsOpen(false);
        },
        [lastTeamScope, onScopeChange, onSegmentChange]
    );

    const handleReportsScopeChange = useCallback(
        (keys: "all" | Set<Key>) => {
            if (keys === "all") {
                return;
            }
            const [key] = keys;
            onScopeChange(key === "direct-reports" ? "direct-reports" : "reporting-line");
        },
        [onScopeChange]
    );

    const handleRequestClose = useCallback(() => setIsOpen(false), []);

    const segmentsSection = useMemo(() => renderSegmentsSection?.({ onRequestClose: handleRequestClose }), [renderSegmentsSection, handleRequestClose]);

    const triggerLabel = isSegmentActive && activeSegmentName ? activeSegmentName : isOrganization ? (session?.organizationName ?? t("scope.organization")) : t("scope.myTeam");

    const myTeamPreviewKey: keyof ScopePreviews = PREVIEW_KEY_BY_SCOPE[isOrganization ? lastTeamScope : scope];

    const selectedKey: Key = isSegmentActive && segmentId ? segmentId : dropdownKey;
    const showOrganizationScope = availableScopes.includes("organization");
    const showReportsPill = !isOrganization && !isSegmentActive && !hasAdHocFilter && availableScopes.includes("direct-reports");

    const triggerButton = (
        <MenuTrigger
            aria-label={t("triggerAriaLabel", { current: triggerLabel })}
            className="group bg-bg border-border text-fg hover:bg-secondary/50 focus-visible:ring-ring/60 focus-visible:border-ring flex h-11 w-64 max-w-full items-center gap-2 rounded-lg border pr-3 pl-2 text-base font-medium outline-hidden transition-colors focus-visible:ring-2"
        >
            {isSegmentActive ? (
                <span data-slot="avatar" className="bg-fg/5 inline-flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full">
                    <UserGroupIcon aria-hidden="true" className="me-0! size-3.5" />
                </span>
            ) : isOrganization ? (
                <OrganizationLogo imageUrl={session?.organizationImage} size="sm" />
            ) : (
                <PersonAvatar person={currentUserPerson} size="sm" />
            )}
            <span className="min-w-0 flex-1 truncate text-sm" title={triggerLabel}>
                {triggerLabel}
            </span>
            <ChevronDownIcon aria-hidden="true" data-slot="chevron" className="text-muted-fg size-4 shrink-0 transition-transform group-aria-expanded:rotate-180" />
        </MenuTrigger>
    );

    const overlayBody = (
        <>
            {segmentsSection?.search}
            <GridList
                aria-label={t("triggerAriaLabel", { current: triggerLabel })}
                selectionMode="single"
                selectedKeys={new Set([selectedKey])}
                onSelectionChange={handleSelection}
                disallowEmptySelection
                escapeKeyBehavior="none"
                className="max-h-96 min-h-0 w-full min-w-56 flex-1 scroll-py-1 divide-y-0 overflow-y-auto overscroll-contain border-0"
            >
                <GridListItem id="reporting-line" textValue={t("scope.myTeam")} className={MENU_ITEM_CLASS_NAME}>
                    {({ isSelected }) => (
                        <>
                            <PersonAvatar person={currentUserPerson} size="md" className="[--avatar-size:--spacing(8)]!" />
                            <span className="flex flex-1 items-center justify-between gap-2">
                                <span data-public>{t("scope.myTeam")}</span>
                                <span className="flex items-center gap-2">
                                    {shouldRenderPreviews && <ScopeCount count={resolvedPreviews[myTeamPreviewKey].totalCount} isLoading={isLoading} />}
                                    <TrailingCheck isSelected={isSelected} />
                                </span>
                            </span>
                        </>
                    )}
                </GridListItem>
                {showOrganizationScope && (
                    <GridListItem id="organization" textValue={t("scope.organization")} className={MENU_ITEM_CLASS_NAME}>
                        {({ isSelected }) => (
                            <>
                                <OrganizationLogo imageUrl={session?.organizationImage} size="md" />
                                <span className="flex flex-1 items-center justify-between gap-2">
                                    <span data-public>{t("scope.organization")}</span>
                                    <span className="flex items-center gap-2">
                                        {shouldRenderPreviews && <ScopeCount count={resolvedPreviews.organization.totalCount} isLoading={isLoading} />}
                                        <TrailingCheck isSelected={isSelected} />
                                    </span>
                                </span>
                            </>
                        )}
                    </GridListItem>
                )}
                {segmentsSection?.rows}
            </GridList>
            {segmentsSection?.footer}
        </>
    );

    return (
        <div className="-mb-0.5 flex w-full flex-wrap items-center gap-4">
            <output className="sr-only">{isLoading && t("liveRegion.loading")}</output>
            {isMobile ? (
                <Sheet isOpen={isOpen} onOpenChange={handleOpenChange}>
                    {triggerButton}
                    <SheetContent side="bottom" isFloat={false} closeButton={false} aria-label={t("triggerAriaLabel", { current: triggerLabel })} className="gap-0 border-0 p-0">
                        {overlayBody}
                    </SheetContent>
                </Sheet>
            ) : (
                <Popover isOpen={isOpen} onOpenChange={handleOpenChange}>
                    {triggerButton}
                    <PopoverContent placement="bottom start" className="w-96 *:data-[slot=popover-inner]:overflow-hidden">
                        {overlayBody}
                    </PopoverContent>
                </Popover>
            )}

            {showReportsPill && (
                <ToggleGroup
                    selectionMode="single"
                    disallowEmptySelection
                    selectedKeys={new Set([scope])}
                    onSelectionChange={handleReportsScopeChange}
                    aria-label={t("reportsScopeAriaLabel")}
                    size="lg"
                    className="[--toggle-selected-bg:var(--color-primary-subtle)] [--toggle-selected-fg:var(--color-primary-subtle-fg)] [&_[data-slot=toggle-group-item][data-selected]:not([data-focus-visible])]:inset-ring-transparent"
                >
                    <ToggleGroupItem id="reporting-line" className="group">
                        <span data-public>{t("scope.allReports")}</span>
                        {shouldRenderPreviews && <SegmentCount count={resolvedPreviews.reportingLine.totalCount} isLoading={isLoading} />}
                    </ToggleGroupItem>
                    <ToggleGroupItem id="direct-reports" className="group">
                        <span data-public>{t("scope.directReports")}</span>
                        {shouldRenderPreviews && <SegmentCount count={resolvedPreviews.directReports.totalCount} isLoading={isLoading} />}
                    </ToggleGroupItem>
                </ToggleGroup>
            )}

            {trailingContent}
        </div>
    );
}
